import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../../api/apiClient";
import {
  FEATURE_CODES,
  FEATURE_PROFILE_CODES,
} from "../../../../constants/constants";
import { useAccess } from "../../../../context/AccessContext";
import { notify } from "../../../components-ui/toast";

interface FeatureProfile {
  featureProfileId: number;
  code: string;
  name: string;
  description?: string;
}

interface TenantAccess {
  tenantId: number;
  profile: FeatureProfile | null;
  features: string[];
}

interface OrganisationProfileStepProps {
  tenantId: number;
}

const FEATURE_NAMES: Record<string, string> = {
  [FEATURE_CODES.SERVICE_DETAILS]: "Service details",
  [FEATURE_CODES.SERVICE_BRANDING]: "Service branding",
  [FEATURE_CODES.SERVICE_ATTRIBUTES]: "Service attributes",
  [FEATURE_CODES.SERVICE_REGISTRATION]: "Service registration",
  [FEATURE_CODES.SERVICE_AGREEMENTS_CONSENTS]: "Agreements and consents",
  [FEATURE_CODES.SERVICE_TEAM_ROLES]: "Service team roles",
  [FEATURE_CODES.SERVICE_GROUPS_CLASSES]: "Groups and classes",
  [FEATURE_CODES.SERVICE_SCHEDULE_SESSIONS]: "Schedule sessions",
  [FEATURE_CODES.SERVICE_SESSION_REPORTS]: "Session reports",
  [FEATURE_CODES.SERVICE_ROTA]: "Service rota",
  [FEATURE_CODES.SERVICE_CALENDAR]: "Service calendar",
  [FEATURE_CODES.SERVICE_ATTENDANCE_CHECK_IN]: "Attendance and check-in",
  [FEATURE_CODES.SERVICE_MEMBERS]: "Service members",
  [FEATURE_CODES.MESSAGING_AUDIENCES_COMMS]: "Audiences and communications",
  [FEATURE_CODES.AI_INTEGRATION]: "AI integration",
};

export default function OrganisationProfileStep({
  tenantId,
}: OrganisationProfileStepProps) {
  const { refresh: refreshCurrentAccess } = useAccess();
  const [profiles, setProfiles] = useState<FeatureProfile[]>([]);
  const [access, setAccess] = useState<TenantAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAccess = useCallback(async () => {
    setAccess(
      await api<TenantAccess>(
        `/api/tenant-administration/${tenantId}/features`,
      ),
    );
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api<FeatureProfile[]>("/api/tenant-administration/feature-profiles"),
      loadAccess(),
    ])
      .then(([profileResult]) => setProfiles(profileResult))
      .catch((error) => {
        console.error("Failed to load organisation profile", error);
        notify.error("Failed to load organisation profile");
      })
      .finally(() => setLoading(false));
  }, [loadAccess]);

  const updateProfile = async (profileCode: string) => {
    try {
      setSaving(true);
      await api<void>(
        `/api/tenant-administration/${tenantId}/feature-profile`,
        { method: "PUT", body: { profileCode } },
      );
      await loadAccess();
      await refreshCurrentAccess();
      notify.success("Organisation profile updated");
    } catch (error) {
      console.error("Failed to update organisation profile", error);
      notify.error("Failed to update organisation profile");
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = async (featureCode: string, isEnabled: boolean) => {
    try {
      setSaving(true);
      await api<void>(
        `/api/tenant-administration/${tenantId}/features/${featureCode}`,
        { method: "PUT", body: { isEnabled } },
      );
      await loadAccess();
      await refreshCurrentAccess();
      notify.success("Organisation features updated");
    } catch (error) {
      console.error("Failed to update organisation feature", error);
      notify.error("Failed to update organisation feature");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card tenant-admin-empty">Loading profile...</div>;
  }

  if (!access) {
    return (
      <div className="card tenant-admin-empty">
        The organisation profile could not be loaded.
      </div>
    );
  }

  const coreFeatures = access.features.filter(
    (feature) => feature !== FEATURE_CODES.AI_INTEGRATION,
  );
  const isServiceManagement =
    access.profile?.code === FEATURE_PROFILE_CODES.SERVICE_MANAGEMENT;

  return (
    <div className="tenant-access-grid">
      <div className="card">
        <div className="card-title">Feature profile</div>
        <div className="form-group">
          <label className="form-label" htmlFor="access-profile">
            Profile
          </label>
          <select
            id="access-profile"
            className="form-input"
            value={access.profile?.code ?? ""}
            disabled={saving}
            onChange={(event) => void updateProfile(event.target.value)}
          >
            <option value="" disabled>Select profile</option>
            {profiles.map((profile) => (
              <option key={profile.code} value={profile.code}>
                {profile.name}
              </option>
            ))}
          </select>
          {access.profile?.description && (
            <p className="tenant-admin-hint">{access.profile.description}</p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Additional features</div>
        <label className="option-item">
          <input
            type="checkbox"
            checked={access.features.includes(FEATURE_CODES.AI_INTEGRATION)}
            disabled={saving || !isServiceManagement}
            onChange={(event) =>
              void updateFeature(
                FEATURE_CODES.AI_INTEGRATION,
                event.target.checked,
              )
            }
          />
          AI integration
        </label>
        {!isServiceManagement && (
          <p className="tenant-admin-hint">
            AI integration is available with the Service Management profile.
          </p>
        )}
      </div>

      <div className="card tenant-access-included-features">
        <div className="card-title">Included features</div>
        <p className="tenant-admin-hint">
          These features are supplied by the selected profile.
        </p>
        <ul>
          {coreFeatures.map((feature) => (
            <li key={feature}>{FEATURE_NAMES[feature] ?? feature}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
